import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { formatPhoneNumber } from '../utils/formatPhone'
import { formatSalary } from '../utils/formatCurrency'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material'
import { leadsApi } from '../services/api'
import { Lead } from '../state/leads'
import AddLeadDialog, { NewLeadData } from '../components/pipeline/AddLeadDialog'
import LeadDetailPanel from '../components/pipeline/LeadDetailPanel'
import { useNotification } from '../hooks/useNotification'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'

const statusColors: Record<string, 'default' | 'info' | 'success' | 'error' | 'secondary' | 'warning'> = {
  new: 'default',
  contacted: 'info',
  qualified: 'success',
  not_qualified: 'error',
  won: 'secondary',
  lost: 'warning',
}

const statusLabels: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  not_qualified: 'Not Qualified',
  won: 'Won',
  lost: 'Lost',
}

export default function Leads() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  // Set once when a deep link has already sent us back for the full list, so the
  // effect below cannot loop on an id that matches nothing.
  const [widenedForDeepLink, setWidenedForDeepLink] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; lead: Lead } | null>(null)
  const { showSuccess, showError } = useNotification()

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const loadLeads = async (all = false) => {
    try {
      setLoading(true)
      setError(null)
      const leads = await leadsApi.list(all ? { all: true } : undefined)
      setLeads(leads ?? [])
    } catch (err) {
      console.error('Error loading leads:', err)
      setError(err instanceof Error ? err.message : 'Failed to load leads')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLeads()
  }, [])

  // Global search sends leads here as /leads?id=<leadId>, which the Sales
  // Pipeline board used to answer before it was removed (MWA-337).
  //
  // This list is scoped to your own leads, but search returns every lead to
  // every staff member, so arriving for someone else's lead is the normal case.
  // Widening reloads, this effect runs again with the lead in hand, and the
  // guard stops it going round twice when the id matches nothing at all.
  useEffect(() => {
    const leadId = searchParams.get('id')
    if (!leadId || loading) return

    const lead = leads.find((l) => l.id === leadId)
    if (lead) {
      setSelectedLead(lead)
      setDetailOpen(true)
      setSearchParams({}, { replace: true })
      return
    }

    if (!widenedForDeepLink) {
      setWidenedForDeepLink(true)
      loadLeads(true)
    }
  }, [searchParams, leads, loading, widenedForDeepLink, setSearchParams])

  const filteredLeads = leads.filter(
    (lead) =>
      lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleAddLead = async (newLeadData: NewLeadData) => {
    try {
      const newLead = await leadsApi.create(newLeadData)
      setLeads([...(leads ?? []), newLead as Lead])
      showSuccess('Lead created successfully')
    } catch (err) {
      console.error('Failed to create lead:', err)
      showError(err instanceof Error ? err.message : 'Failed to create lead')
      throw err
    }
  }

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead)
    setDetailOpen(true)
  }

  const handleSaveLead = async (lead: Lead) => {
    try {
      await leadsApi.update(lead)
      showSuccess('Lead updated successfully')
      setDetailOpen(false)
      setSelectedLead(null)
      // Stay widened if a deep link put us there, or saving someone else's lead
      // would drop it out of the table it was just edited in.
      loadLeads(widenedForDeepLink)
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update lead')
    }
  }

  const handleCloseDetail = () => {
    setDetailOpen(false)
    setSelectedLead(null)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: { xs: 1, sm: 1 } }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Leads</Typography>
          <PageHelpButton articleId="page-leads-crm" />
        </Stack>
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
            onClick={() => loadLeads(widenedForDeepLink)}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
            <RefreshIcon sx={{ display: { xs: 'inline-block', sm: 'none' } }} />
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
            onClick={() => setAddDialogOpen(true)}
            size={isMobile ? 'small' : 'medium'}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Lead</Box>
            <AddIcon sx={{ display: { xs: 'inline-block', sm: 'none' } }} />
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <TextField
              placeholder="Search leads..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>

          {filteredLeads.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {leads.length === 0 ? 'No leads yet' : 'No leads match your search'}
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile Card Layout
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredLeads.map((lead) => (
                <Card
                  key={lead.id}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                  onClick={() => handleEditLead(lead)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '0.875rem' }}>
                        {getInitials(lead.name || 'L')}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                            {lead.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor({ el: e.currentTarget, lead })
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Chip
                          label={statusLabels[lead.status] || lead.status}
                          size="small"
                          color={statusColors[lead.status] || 'default'}
                          sx={{ height: 20, fontSize: '0.7rem', mb: 1 }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {lead.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                            {lead.email}
                          </Typography>
                        </Box>
                      )}
                      {lead.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                            {formatPhoneNumber(lead.phone)}
                          </Typography>
                        </Box>
                      )}
                      {lead.source && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Source: {lead.source}
                        </Typography>
                      )}
                      {lead.salaryTarget && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Salary Target: {formatSalary(lead.salaryTarget)}
                        </Typography>
                      )}
                      {lead.assignedTo && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Assigned to: {lead.assignedTo}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            // Desktop Table Layout
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Salary Target</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{lead.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{lead.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatPhoneNumber(lead.phone) || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{lead.source}</TableCell>
                      <TableCell>{formatSalary(lead.salaryTarget) || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[lead.status] || lead.status}
                          size="small"
                          color={statusColors[lead.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>{lead.assignedTo || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, lead })}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <AddLeadDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddLead}
      />

      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          if (menuAnchor) handleEditLead(menuAnchor.lead)
          setMenuAnchor(null)
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit Details</ListItemText>
        </MenuItem>
      </Menu>

      <LeadDetailPanel
        open={detailOpen}
        lead={selectedLead}
        onClose={handleCloseDetail}
        onSave={handleSaveLead}
        onQualify={() => {}}
        onConvert={() => {}}
        infoOnly
      />
    </Box>
  )
}
